// ============================================================
//  src/home.js — Home pública (/)
// ============================================================

import { db } from './firebase.js';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';

const MAX_AVATARES  = 12;
const MAX_ACTIVIDAD = 10;

// Reemplazar con el endpoint real de Formspree (https://formspree.io/f/XXXXXXXX)
const FORMSPREE = 'https://formspree.io/f/XXXXXXXX';

// ── Helpers ──────────────────────────────────────────────────

function shuffleArray(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function tiempoRelativo(ts, lang = 'es') {
    if (!ts) return '';
    const date = ts?.toDate ? ts.toDate() : new Date(ts);
    const diff  = Date.now() - date.getTime();
    const min   = Math.floor(diff / 60000);
    if (lang === 'en') {
        if (min < 2)  return 'just now';
        if (min < 60) return `${min} min ago`;
        const h = Math.floor(min / 60);
        if (h < 24)   return `${h}h ago`;
        const d = Math.floor(h / 24);
        if (d === 1)  return 'yesterday';
        if (d < 7)    return `${d} days ago`;
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    }
    if (min < 2)  return 'hace un momento';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24)   return `hace ${h}h`;
    const d = Math.floor(h / 24);
    if (d === 1)  return 'ayer';
    if (d < 7)    return `hace ${d} días`;
    return date.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' });
}

function avatarImg(src, cs, size) {
    const s = `width:${size}px;height:${size}px`;
    if (src) return `<img src="${src}" alt="${cs}" style="${s};border-radius:50%;object-fit:cover;" loading="lazy">`;
    const fs = Math.round(size * 0.42);
    return `<div style="${s};border-radius:50%;background:#1e2130;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:${fs}px;color:#00d4aa;">${(cs || '?').charAt(0)}</div>`;
}

// ── Comunidad ─────────────────────────────────────────────────

async function renderComunidad() {
    const container = document.getElementById('community-grid');
    if (!container) return;

    try {
        const snap     = await getDocs(collection(db, 'publicProfiles'));
        const todos    = snap.docs.map(d => d.data()).filter(p => p.callsign && p.resumenTarjeta?.identidad);
        const seleccion = shuffleArray(todos).slice(0, MAX_AVATARES);

        if (!seleccion.length) {
            container.closest('section')?.remove();
            return;
        }

        container.innerHTML = seleccion.map(p => {
            const id = p.resumenTarjeta.identidad;
            const cs = p.callsign;
            return `<a href="https://app.contestmanager.uy/embed/${cs}" class="hc-avatar" title="${id.nombreVisible || cs}">
                ${avatarImg(id.avatar, cs, 52)}
                <span class="hc-avatar__cs">${cs}</span>
            </a>`;
        }).join('');
    } catch (e) {
        console.error('[Home] comunidad', e);
        container.closest('section')?.remove();
    }
}

// ── Actividad ─────────────────────────────────────────────────

const TEXTOS_TIPO = {
    es: {
        avatar:      'actualizó su avatar',
        certificado: 'recibió un certificado',
        shack:       'actualizó su shack',
        perfil:      'actualizó su perfil',
    },
    en: {
        avatar:      'updated their avatar',
        certificado: 'received a certificate',
        shack:       'updated their shack',
        perfil:      'updated their profile',
    },
};

let _actividadCache = null;

function _pintarActividad(items, lang, container) {
    const textos   = TEXTOS_TIPO[lang] || TEXTOS_TIPO.es;
    const emptyMsg = lang === 'en' ? 'No recent activity.' : 'Sin actividad reciente.';

    if (!items.length) {
        container.innerHTML = `<p class="ha-empty">${emptyMsg}</p>`;
        return;
    }

    container.innerHTML = items.map(p => {
        const id    = p.resumenTarjeta.identidad;
        const cs    = p.callsign;
        const tipo  = p.ultimaActividad?.tipo || 'perfil';
        const texto = (textos[tipo] || textos.perfil);
        const tiempo = tiempoRelativo(p.actualizadoEn, lang);

        return `<a href="https://app.contestmanager.uy/embed/${cs}" class="ha-item">
            <div class="ha-item__av">${avatarImg(id.avatar, cs, 38)}</div>
            <div class="ha-item__body">
                <span class="ha-item__cs">${cs}</span>
                <span class="ha-item__txt">${texto}</span>
            </div>
            <span class="ha-item__ts">${tiempo}</span>
        </a>`;
    }).join('');
}

async function renderActividad(lang = 'es') {
    const container = document.getElementById('activity-feed');
    if (!container) return;

    // Si ya tenemos datos en caché, re-pintar al instante sin re-fetch
    if (_actividadCache) {
        _pintarActividad(_actividadCache, lang, container);
        return;
    }

    try {
        const q    = query(collection(db, 'publicProfiles'), orderBy('actualizadoEn', 'desc'), limit(MAX_ACTIVIDAD));
        const snap = await getDocs(q);
        const items = snap.docs
            .map(d => d.data())
            .filter(p => p.callsign && p.resumenTarjeta?.identidad && p.actualizadoEn);

        _actividadCache = items;
        _pintarActividad(items, lang, container);
    } catch (e) {
        console.error('[Home] actividad', e);
        container.closest('section')?.remove();
    }
}


// ── i18n mínimo ───────────────────────────────────────────────

const T = {
    es: {
        contact_trigger:      'Contáctanos',
        contact_modal_title:  'Envíanos tu sugerencia',
        form_nombre:          'Nombre',
        form_email:           'Tu email',
        form_mensaje:         'Mensaje',
        form_enviar:          'Enviar sugerencia',
        form_sending:         'Enviando…',
        form_ok:              '¡Mensaje enviado! Gracias por tu sugerencia.',
        form_err:             'Error al enviar. Inténtalo de nuevo.',
        form_required:        'Completa todos los campos obligatorios.',
        hero_body1:         'Contest Manager es una herramienta pensada para radioaficionados que participan en concursos. Permite organizar, centralizar y dar valor a toda tu actividad: logs, resultados, certificados y participaciones.',
        hero_body2:         'Hoy esa información está dispersa, se pierde o queda difícil de mostrar. Contest Manager busca resolver eso, dándote un espacio claro, ordenado y público para mostrar tu recorrido como operador.',
        hero_cta1:          'Entrar a la app',
        hero_cta2:          'Crear cuenta gratis',
        why_title:          '¿Por qué usar Contest Manager?',
        why_1:              'Centraliza toda tu actividad en concursos',
        why_2:              'Visualiza tu progreso a lo largo del tiempo',
        why_3:              'Accede rápido a tus resultados y certificados',
        why_4:              'Comparte tu perfil público con otros operadores (QRZ, redes, etc.)',
        why_5:              'Construye tu historial como radioaficionado',
        community_title:    'Comunidad',
        community_sub:      'Operadores que ya usan Contest Manager',
        activity_title:     'Actividad reciente',
        activity_sub:       'Lo último de la comunidad',
        wip_title:          'En construcción',
        wip_body1:          'Contest Manager está en desarrollo activo. Se irán sumando nuevas funciones, mejoras y herramientas.',
        wip_body2:          'Si tienes sugerencias o ideas, son bienvenidas.',
        wip_body2:          'Si tienes sugerencias o ideas, son bienvenidas.',
    },
    en: {
        contact_trigger:      'Contact us',
        contact_modal_title:  'Send us your suggestion',
        form_nombre:          'Name',
        form_email:           'Your email',
        form_mensaje:         'Message',
        form_enviar:          'Send suggestion',
        form_sending:         'Sending…',
        form_ok:              'Message sent! Thanks for your suggestion.',
        form_err:             'Error sending. Please try again.',
        form_required:        'Please fill in all required fields.',
        hero_body1:         'Contest Manager is a tool designed for amateur radio operators who participate in contests. It lets you organize, centralize and showcase your activity: logs, results, certificates and participations.',
        hero_body2:         'Today that information is scattered, gets lost or is hard to share. Contest Manager aims to fix that, giving you a clear, organized and public space to show your journey as an operator.',
        hero_cta1:          'Open app',
        hero_cta2:          'Create free account',
        why_title:          'Why use Contest Manager?',
        why_1:              'Centralize all your contest activity',
        why_2:              'Track your progress over time',
        why_3:              'Access your results and certificates quickly',
        why_4:              'Share your public profile with other operators (QRZ, social media, etc.)',
        why_5:              'Build your amateur radio history',
        community_title:    'Community',
        community_sub:      'Operators already using Contest Manager',
        activity_title:     'Recent activity',
        activity_sub:       'Latest from the community',
        wip_title:          'Under construction',
        wip_body1:          'Contest Manager is in active development. New features, improvements and tools will be added over time.',
        wip_body2:          'Suggestions and ideas are welcome.',
    },
};

function applyLang(lang) {
    const d = T[lang] || T.es;
    document.documentElement.lang = lang;
    document.querySelectorAll('[data-t]').forEach(el => {
        const v = d[el.dataset.t];
        if (v !== undefined) el.textContent = v;
    });
    document.getElementById('lang-es')?.classList.toggle('active', lang === 'es');
    document.getElementById('lang-en')?.classList.toggle('active', lang === 'en');
}

const APP_ORIGIN = 'https://app.contestmanager.uy';

function _actualizarLinksApp(lang) {
    document.querySelectorAll(`a[href^="${APP_ORIGIN}"]`).forEach(a => {
        const url = new URL(a.href);
        url.searchParams.set('lang', lang);
        a.href = url.toString();
    });
}

function setLang(lang) {
    localStorage.setItem('idioma', lang);
    _actualizarLinksApp(lang);
    applyLang(lang);
    renderActividad(lang);
}

// ── Formulario de sugerencias ─────────────────────────────────

function closeModal() {
    const overlay = document.getElementById('contact-overlay');
    if (overlay) { overlay.hidden = true; document.body.style.overflow = ''; }
    document.getElementById('sf-feedback').hidden = true;
    document.getElementById('suggestion-form')?.reset();
}

function initModal() {
    const overlay = document.getElementById('contact-overlay');
    const trigger = document.getElementById('contact-trigger');
    if (!overlay || !trigger) return;

    const open = () => { overlay.hidden = false; document.body.style.overflow = 'hidden'; };

    trigger.addEventListener('click', open);
    document.getElementById('modal-close')?.addEventListener('click', closeModal);
    overlay.addEventListener('click', e => { if (e.target === overlay) closeModal(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !overlay.hidden) closeModal(); });
}

function initForm() {
    const form = document.getElementById('suggestion-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn      = form.querySelector('.h-form__submit');
        const feedback = document.getElementById('sf-feedback');
        const lang     = localStorage.getItem('idioma') || 'es';
        const d        = T[lang] || T.es;
        const name     = form.querySelector('#sf-name').value.trim();
        const email    = form.querySelector('#sf-email').value.trim();
        const msg      = form.querySelector('#sf-msg').value.trim();

        if (!name || !email || !msg) {
            feedback.textContent = d.form_required;
            feedback.className   = 'h-form__feedback h-form__feedback--err';
            feedback.hidden      = false;
            return;
        }

        btn.disabled    = true;
        btn.textContent = d.form_sending;
        feedback.hidden = true;

        try {
            const res = await fetch(FORMSPREE, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    name,
                    email,
                    message:  msg,
                    _subject: 'Sugerencia — Contest Manager',
                }),
            });

            if (res.ok) {
                feedback.textContent = d.form_ok;
                feedback.className   = 'h-form__feedback h-form__feedback--ok';
                feedback.hidden      = false;
                form.reset();
                setTimeout(closeModal, 2500);
            } else {
                throw new Error(res.status);
            }
        } catch {
            feedback.textContent = d.form_err;
            feedback.className   = 'h-form__feedback h-form__feedback--err';
            feedback.hidden      = false;
        } finally {
            btn.disabled    = false;
            btn.textContent = d.form_enviar;
        }
    });
}

// ── Init ──────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    const lang = localStorage.getItem('idioma') || 'es';
    applyLang(lang);
    _actualizarLinksApp(lang);

    document.getElementById('lang-es')?.addEventListener('click', () => setLang('es'));
    document.getElementById('lang-en')?.addEventListener('click', () => setLang('en'));
    window.addEventListener('storage', e => { if (e.key === 'idioma') applyLang(e.newValue || 'es'); });

    renderComunidad();
    renderActividad(lang);
    initModal();
    initForm();
});
